import { useCallback, useMemo } from "react";
import { getBaseUrl } from "@Front/Api/helpers/configHelpers";
import type { UseApiClientReturn } from "@Front/Hooks/useApiClientTypes";
import { buildRequestHeaders } from "@Front/Hooks/useApiClientRequestCore";
import { getAccessTokenFromStores } from "@Front/Hooks/useApiClientAccessToken";
import { useApiRequestMethods } from "@Front/Hooks/useApiClientMethods";

export type { ApiRequestOptions, ApiResponse, ApiError, UseApiClientReturn } from "@Front/Hooks/useApiClientTypes";

/**
 * Hook for making API requests with standardized token/variables management,
 * headers, and error handling
 */
export function useApiClient(): UseApiClientReturn {
	const getAccessToken = useCallback((endpoint?: string): string | null => getAccessTokenFromStores(endpoint), []);

	const baseUrl = useMemo(() => getBaseUrl(), []);

	const getHeaders = useCallback(
		(requireAuth: boolean = true, additionalHeaders?: HeadersInit, endpoint?: string): Headers => {
			return buildRequestHeaders(requireAuth, getAccessToken, additionalHeaders, endpoint);
		},
		[getAccessToken],
	);

	const methods = useApiRequestMethods(baseUrl, getAccessToken, getHeaders);

	return useMemo(
		() => ({
			baseUrl,
			getToken: getAccessToken,
			getHeaders,
			...methods,
		}),
		[baseUrl, getAccessToken, getHeaders, methods],
	);
}
